import { useApi } from "@ametie/vue-muza-use";
import { useToast } from "@app/core";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

// import { useApi } from "@/shared/composables/useApi";

// Types
export interface SavedFilter {
  id: string
  userId: string
  name: string
  link: string
  favorite: boolean
  date: string
}

export interface CreateFilterPayload {
  name: string
  link: string
  favorite: boolean
}

/**
 * Composable for managing saved user filters
 * Handles CRUD operations for filter presets
 */
export function useSavedFilters() {
  const router = useRouter();
  const { success, error } = useToast();

  // State
  const filters = ref<SavedFilter[]>([]);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const isDeleting = ref<string | null>(null);
  const isTogglingFavorite = ref<string | null>(null);

  // Sorted filters: favorites first, then by date
  const sortedFilters = computed(() => {
    return [...filters.value].sort((a, b) => {
      if (a.favorite !== b.favorite) {
        return a.favorite ? -1 : 1;
      }
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  });

  /**
     * Fetch all saved filters
     */
  const fetchFilters = async () => {
    isLoading.value = true;

    const { execute } = useApi<SavedFilter[]>("/user-filters", {
      useGlobalAbort: false,
    });

    const data = await execute();
    if (data) {
      filters.value = data;
    }

    isLoading.value = false;
  };

  /**
     * Create a new saved filter
     */
  const createFilter = async (payload: CreateFilterPayload): Promise<boolean> => {
    isCreating.value = true;

    const { execute } = useApi<SavedFilter>("/user-filters/save-filter", {
      method: "POST",
      useGlobalAbort: false,
    });

    const data = await execute({ data: payload });

    isCreating.value = false;

    if (data) {
      success(`Filter "${payload.name}" saved successfully`);
      await fetchFilters();
      return true;
    }

    error("Failed to save filter");
    return false;
  };

  /**
     * Delete a saved filter
     */
  const deleteFilter = async (id: string): Promise<boolean> => {
    isDeleting.value = id;

    const { execute } = useApi(`/user-filters/${id}`, {
      method: "DELETE",
      useGlobalAbort: false,
    });

    const data = await execute();

    isDeleting.value = null;

    if (data !== null) {
      success("Filter deleted successfully");
      filters.value = filters.value.filter(f => f.id !== id);
      return true;
    }

    error("Failed to delete filter");
    return false;
  };

  /**
     * Toggle favorite status
     */
  const toggleFavorite = async (id: string): Promise<boolean> => {
    const filter = filters.value.find(f => f.id === id);
    if (!filter) return false;

    isTogglingFavorite.value = id;
    const newFavorite = !filter.favorite;

    const { execute } = useApi("/user-filters/set-favorite", {
      method: "PATCH",
      useGlobalAbort: false,
    });

    const data = await execute({
      data: { id, favorite: newFavorite },
    });

    isTogglingFavorite.value = null;

    if (data !== null) {
      filter.favorite = newFavorite;
      return true;
    }

    error("Failed to update favorite");
    return false;
  };

  /**
     * Apply a saved filter (update URL query params without changing page)
     */
  const applyFilter = (filter: SavedFilter) => {
    // Parse the saved link (which is only query string like "?brands=X&channels=Y")
    const searchParams = new URLSearchParams(filter.link.startsWith("?") ? filter.link.slice(1) : filter.link);

    // Build query object
    const query: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      query[key] = value;
    });

    // Navigate to current page with new query params
    router.push({
      path: router.currentRoute.value.path,
      query,
    });
  };

  /**
     * Build current filter link from URL (only query params, no path)
     * @param excludeParams - Parameters to exclude from the link
     */
  const buildCurrentFilterLink = (excludeParams: string[] = []): string => {
    const currentQuery = { ...router.currentRoute.value.query };

    // Remove excluded params
    for (const param of excludeParams) {
      delete currentQuery[param];
    }

    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(currentQuery)) {
      if (value) {
        searchParams.set(key, String(value));
      }
    }

    // Return only query string (e.g., "?brands=X&channels=Y")
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  };

  return {
    // State
    filters,
    sortedFilters,
    isLoading,
    isCreating,
    isDeleting,
    isTogglingFavorite,

    // Actions
    fetchFilters,
    createFilter,
    deleteFilter,
    toggleFavorite,
    applyFilter,
    buildCurrentFilterLink,
  };
}
